import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, ArrowUpRight, Boxes, FileText, Loader2, PlusCircle, Sparkles, TrendingUp,
  Trophy, Wallet, X,
} from "lucide-react";
import {
  Area, AreaChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { api, apiError, inr, logError, shortDate } from "@/lib/api";import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";

const AXIS_TICK = { fontSize: 11 };
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const PERIODS = [
  ["today", "Today"], ["week", "Week"], ["month", "Month"], ["quarter", "Quarter"], ["fy", "FY"],
];
const COLORS = ["#12513B", "#C09A3E", "#2E7A5C", "#8A6D2F", "#4E9E7E", "#6B4F1D"];

/** Month-on-month change from the trend series; undefined when there is no history yet. */
function pctChange(series, key) {
  if (!series || series.length < 2) return undefined;
  const prev = Number(series[series.length - 2]?.[key] || 0);
  const last = Number(series[series.length - 1]?.[key] || 0);
  if (!prev) return undefined;
  return Math.round(((last - prev) / prev) * 100);
}

function Kpi({ label, value, sub, testId, accent, spark, trend }) {
  return (
    <div data-testid={testId} className="kpi-card">
      <div className="flex items-start justify-between gap-2">
        <div className="overline">{label}</div>
        {typeof trend === "number" && (
          <span
            data-testid={`${testId}-trend`}
            className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold ${
              trend >= 0
                ? "bg-emerald-500/12 text-emerald-600"
                : "bg-destructive/12 text-destructive"
            }`}
          >
            {trend >= 0 ? "▲" : "▼"} {Math.abs(trend)}%
          </span>
        )}
      </div>
      <div className="flex items-end justify-between gap-2">
        <div className={`mt-2 font-mono-num text-2xl font-bold ${accent || ""}`}>{value}</div>
        {spark?.length > 1 && (
          <div className="h-8 w-20 shrink-0" data-testid={`${testId}-spark`}>
            <ResponsiveContainer width="100%" height="100%" minHeight={28}>
              <AreaChart data={spark}>
                <Area type="monotone" dataKey="v" stroke="hsl(var(--accent))"
                  fill="hsl(var(--accent) / 0.22)" strokeWidth={1.5} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function CustomerEditApprovals() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    api.get("/customer-admin/pending-edits")
      .then(({ data }) => setCount(data.can_approve ? data.total : 0))
      .catch(() => setCount(0));
  }, []);
  if (!count) return null;
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-border bg-secondary px-4 py-2.5 sm:px-6"
      data-testid="dashboard-customer-edit-approvals">
      <span className="min-w-0 flex-1 text-sm font-medium">
        {count} customer detail change{count > 1 ? "s" : ""} waiting for your approval
      </span>
      <Button asChild size="sm" variant="outline" data-testid="dashboard-customer-edit-action">
        <Link to="/customers">Review edits</Link>
      </Button>
    </div>
  );
}

function CriticalAlert({ alerts }) {
  const [dismissed, setDismissed] = useState(false);
  const overdue = alerts?.overdue_invoices || [];
  if (dismissed || overdue.length === 0) return null;
  const total = overdue.reduce((s, i) => s + Number(i.balance_due || i.grand_total || 0), 0);
  return (
    <div className="alert-critical flex flex-wrap items-center gap-3 px-4 py-2.5 sm:px-6"
      data-testid="critical-alert-banner">
      <span className="rounded-full bg-amber-300 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#5b1010]">
        ⚠ Critical
      </span>
      <span className="min-w-0 flex-1 text-sm font-semibold">
        {overdue.length} overdue payment{overdue.length > 1 ? "s require" : " requires"} immediate attention
        <span className="ml-2 font-normal opacity-90">{inr(total)} outstanding</span>
      </span>
      <Button asChild size="sm" variant="secondary"
        className="h-7 rounded-full text-[10px] font-bold uppercase tracking-wider"
        data-testid="critical-alert-action">
        <Link to="/receivables">Overview — Urgent</Link>
      </Button>
      <button type="button" aria-label="Dismiss alert" data-testid="critical-alert-dismiss"
        className="opacity-80 hover:opacity-100" onClick={() => setDismissed(true)}>
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

function QuoteSpotlight() {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/quote/today").then(({ data: d }) => setData(d))
      .catch((e) => logError("quote spotlight", e));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;

  const share = async () => {
    setSaving(true);
    try {
      await api.post("/quotes", { text });
      setText("");
      load();
      toast.success("Added to the team's collection");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="border-b border-border px-4 py-5 sm:px-6" data-testid="quote-spotlight">
      <div className="overline flex items-center gap-1.5">
        <Sparkles className="h-3 w-3 text-primary" /> Thought of the day
      </div>
      <blockquote className="mt-2 max-w-3xl">
        <p className="font-display text-lg leading-snug sm:text-xl" data-testid="spotlight-text">
          “{data.quote.text}”
        </p>
        <footer className="mt-1 text-xs text-muted-foreground" data-testid="spotlight-credit">
          — {data.quote.author || "Unknown"}
          {data.quote.added_by ? ` • shared by ${data.quote.added_by}` : ""}
          {` • ${data.total_quotes} in the collection`}
        </footer>
      </blockquote>
      <div className="mt-3 flex max-w-lg items-center gap-2">
        <Input
          data-testid="spotlight-quote-input"
          placeholder="Add yours for the team…"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <Button
          size="sm"
          variant="outline"
          data-testid="spotlight-quote-save"
          disabled={text.trim().length < 8 || saving}
          onClick={share}
        >
          Share
        </Button>
      </div>
    </section>
  );
}

function StarOfTheMonth() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/performance/star")
      .then(({ data: d }) => setData(d))
      .catch((e) => logError("star of the month", e));
  }, []);

  if (!data?.star) return null;
  const s = data.star;
  return (
    <section className="px-4 pt-5 sm:px-6" data-testid="star-of-month">
      <div className="relative overflow-hidden rounded-2xl border border-accent/40 bg-gradient-to-r from-accent/15 via-transparent to-transparent p-5">
        <Trophy className="absolute right-4 top-4 h-12 w-12 text-accent/25" />
        <div className="overline text-accent">Star of the month • {data.month}</div>
        <div className="mt-1 flex flex-wrap items-baseline gap-2">
          <span className="text-2xl font-semibold" data-testid="star-name">{s.name}</span>
          <span className="text-xs text-muted-foreground">
            {s.employee_code}{s.designation ? ` • ${s.designation}` : ""}
            {s.department ? ` • ${s.department}` : ""}
          </span>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <span className="rounded-full px-2.5 py-0.5 text-xs font-semibold"
            style={{ background: `${s.colour}22`, color: s.colour }} data-testid="star-score">
            {s.display}
          </span>
          <span className="text-xs text-muted-foreground">
            {s.facts.present_days}/{s.facts.applicable_days} days • {s.facts.worked_hours}h worked
            {s.facts.ot_hours ? ` • ${s.facts.ot_hours}h OT` : ""}
          </span>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="star-highlights">
          {s.highlights.map((h) => (
            <span key={h.label} className="rounded-full border border-border px-2 py-0.5 text-[11px]">
              {h.label} {h.percent}%
            </span>
          ))}
        </div>
        {data.runners_up.length > 0 && (
          <p className="mt-2 text-xs text-muted-foreground" data-testid="star-runners">
            Close behind: {data.runners_up.map((r) => `${r.name} (${r.percent}%)`).join(", ")}
          </p>
        )}
      </div>
    </section>
  );
}

export default function Dashboard() {
  const { can, user } = useAuth();
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hotLeads, setHotLeads] = useState(0);

  useEffect(() => {
    if (!can("leads")) return;
    api.get("/leads/hot-count").then(({ data: d }) => setHotLeads(d.hot_count || 0))
      .catch((e) => logError("hot leads", e));
  }, [can]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/dashboard?period=${period}`)
      .then(({ data: fetched }) => {
        if (!cancelled) setData(fetched);
      })
      .catch((e) => logError("dashboard", e))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [period]);

  if (loading && !data)
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );

  const t = data?.today || {};
  const p = data?.period_stats || {};
  const a = data?.alerts || {};

  const quick = [
    ["New Quotation", "/quotations/new", "quotations", "create"],
    ["New Lead", "/leads", "leads", "create"],
    ["New Customer", "/customers", "customers", "create"],
    ["Add Expense", "/expenses", "expenses", "create"],
    ["Stock Movement", "/inventory", "inventory", "edit"],
    ["Purchase Request", "/purchase", "purchase", "create"],
    ["AI Requests", "/ai-requests", "ai", "view"],
  ].filter(([, , m, act]) => can(m, act));

  return (
    <div>
      <PageHeader
        testId="dashboard-header"
        title="Good day, Pramsia"
        subtitle="Live business position across sales, money, stock and employee operations."
        actions={
          <Tabs value={period} onValueChange={setPeriod}>
            <TabsList data-testid="dashboard-period-tabs" className="filter-pills">
              {PERIODS.map(([v, l]) => (
                <TabsTrigger key={v} value={v} data-testid={`period-${v}`}>
                  {l}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        }
      />

      <div className="flex flex-wrap gap-2.5 border-b border-border px-4 py-4 sm:px-6">
        {quick.map(([label, to]) => (
          <div key={label} className="relative">
            {label === "New Lead" && hotLeads > 0 && (
              <span
                data-testid="hot-lead-badge"
                className="badge-hot absolute -top-2 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold shadow"
              >
                🔥 {hotLeads} HOT
              </span>
            )}
            <Link
              to={to}
              data-testid={`quick-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className="pill-action inline-flex items-center gap-1.5"
            >
              <PlusCircle className="h-3.5 w-3.5" /> {label}
            </Link>
          </div>
        ))}
      </div>

      <QuoteSpotlight />

      <StarOfTheMonth />

      <CriticalAlert alerts={a} />

      <CustomerEditApprovals />

      <section>
        <div className="border-b border-border px-4 pt-5 sm:px-6">
          <div className="overline">Today</div>
        </div>
        <div className="grid grid-cols-2 gap-3 px-4 py-4 sm:grid-cols-3 sm:px-6 lg:grid-cols-6">
          <Kpi label="Sales" value={inr(t.sales)} testId="kpi-today-sales" />
          <Kpi label="Collections" value={inr(t.collections)} testId="kpi-today-collections" />
          <Kpi label="Expenses" value={inr(t.expenses)} testId="kpi-today-expenses" />
          <Kpi label="New Leads" value={t.new_leads ?? 0} testId="kpi-today-leads" />
          <Kpi label="Quotations" value={t.new_quotations ?? 0} testId="kpi-today-quotations" />
          <Kpi
            label="Approvals"
            value={t.pending_approvals ?? 0}
            sub={`${t.ai_requests ?? 0} AI • ${t.low_stock ?? 0} low stock`}
            accent={t.pending_approvals ? "text-accent" : ""}
            testId="kpi-today-approvals"
          />
        </div>
      </section>

      <section>
        <div className="border-b border-border px-4 pt-5 sm:px-6">
          <div className="overline">This {period}</div>
        </div>
        <div className="grid grid-cols-2 gap-3 px-4 py-4 sm:grid-cols-3 sm:px-6 lg:grid-cols-4">
          <Kpi label="Sales Value" value={inr(p.sales)} sub={`${p.won_count ?? 0} won`} testId="kpi-sales" trend={pctChange(data?.charts?.sales_trend, "orders")}
            spark={(data?.charts?.sales_trend || []).map((r) => ({ v: r.orders }))} />
          <Kpi label="Collections" value={inr(p.collections)} testId="kpi-collections" trend={pctChange(data?.charts?.sales_trend, "quotations")}
            spark={(data?.charts?.sales_trend || []).map((r) => ({ v: r.quotations }))} />
          <Kpi label="Expenses" value={inr(p.expenses)} testId="kpi-expenses" />
          <Kpi
            label="Est. Net Profit"
            value={inr(p.net_profit)}
            sub={`Gross ${inr(p.gross_profit)}`}
            accent={Number(p.net_profit) < 0 ? "text-destructive" : "text-emerald-600"}
            testId="kpi-net-profit"
          />
          <Kpi label="Receivables" value={inr(p.receivables)} testId="kpi-receivables" />
          <Kpi
            label="Quotation Pipeline"
            value={inr(p.quotation_pipeline)}
            sub={`${p.quotation_count ?? 0} quotes • ${p.conversion_rate ?? 0}% conversion`}
            testId="kpi-pipeline"
          />
          <Kpi
            label="Active Projects"
            value={p.active_projects ?? 0}
            sub={inr(p.project_value)}
            testId="kpi-projects"
          />
          <Kpi label="Stock Value" value={inr(p.stock_value)} sub={`Purchases ${inr(p.purchase_value)}`} testId="kpi-stock-value" />
        </div>
      </section>

      <section className="grid gap-4 p-4 sm:p-6 lg:grid-cols-3">
        <div className="surface p-5 lg:col-span-2" data-testid="chart-sales-trend">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <div className="overline">Order vs quotation value</div>
              <h3 className="font-display text-lg font-semibold">Business trend</h3>
            </div>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%" minHeight={200}>
              <AreaChart data={data?.charts?.sales_trend || []}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#12513B" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#12513B" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C09A3E" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#C09A3E" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="month" tick={AXIS_TICK} stroke="hsl(var(--muted-foreground))" />
                <YAxis
                  tick={AXIS_TICK}
                  stroke="hsl(var(--muted-foreground))"
                  tickFormatter={(v) => `${(v / 100000).toFixed(0)}L`}
                />
                <Tooltip formatter={(v) => inr(v)} />
                <Area type="monotone" dataKey="quotations" stroke="#C09A3E" fill="url(#g2)" strokeWidth={2} />
                <Area type="monotone" dataKey="orders" stroke="#12513B" fill="url(#g1)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="surface p-5" data-testid="chart-quotation-status">
          <div className="overline">Quotation mix</div>
          <h3 className="mb-2 font-display text-lg font-semibold">Pipeline status</h3>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%" minHeight={180}>
              <PieChart>
                <Pie
                  data={data?.charts?.quotation_status || []}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={45}
                  outerRadius={75}
                  paddingAngle={2}
                >
                  {(data?.charts?.quotation_status || []).map((entry, i) => (
                    <Cell key={entry.name} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1">
            {(data?.charts?.quotation_status || []).map((s, i) => (
              <div key={s.name} className="flex items-center gap-2 text-xs">
                <span className="h-2 w-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                <span className="flex-1 truncate">{s.name}</span>
                <span className="font-mono-num">{s.value}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="grid gap-4 px-4 pb-8 sm:px-6 lg:grid-cols-3">
        <AlertCard
          testId="alert-ai-requests"
          icon={Sparkles}
          title="AI employee requests"
          empty="No pending employee requests"
          link="/ai-requests"
          rows={(a.ai_requests || []).map((r) => ({
            key: r.id,
            main: `${r.employee_name} • ${r.intent}`,
            sub: r.summary,
            tag: `${r.confidence}%`,
          }))}
        />
        <AlertCard
          testId="alert-low-stock"
          icon={Boxes}
          title="Low stock"
          empty="All materials above reorder level"
          link="/inventory"
          rows={(a.low_stock || []).map((r) => ({
            key: r.id,
            main: r.name,
            sub: `${r.quantity} ${r.unit} • reorder ${r.reorder_level}`,
            tag: "LOW",
          }))}
        />
        <AlertCard
          testId="alert-overdue"
          icon={Wallet}
          title="Overdue payments"
          empty="No overdue invoices"
          link="/receivables"
          rows={(a.overdue_invoices || []).map((r) => ({
            key: r.id,
            main: r.customer_name,
            sub: `${r.number} • due ${shortDate(r.due_date)}`,
            tag: inr(Number(r.grand_total) - Number(r.paid_amount || 0)),
          }))}
        />
        <AlertCard
          testId="alert-pending-quotations"
          icon={FileText}
          title="Quotations awaiting approval"
          empty="Nothing waiting on approval"
          link="/quotations"
          rows={(a.pending_quotations || []).map((r) => ({
            key: r.id,
            main: r.customer_name,
            sub: r.number,
            tag: inr(r.grand_total),
          }))}
        />
        <AlertCard
          testId="alert-expiring"
          icon={AlertTriangle}
          title="Expiring quotations"
          empty="No quotations expiring this week"
          link="/quotations"
          rows={(a.expiring_quotations || []).map((r) => ({
            key: r.id,
            main: r.customer_name,
            sub: `${r.number} • valid till ${shortDate(r.valid_until)}`,
            tag: inr(r.grand_total),
          }))}
        />
        <AlertCard
          testId="alert-purchase"
          icon={AlertTriangle}
          title="Purchase requests"
          empty="No purchase requests pending"
          link="/purchase"
          rows={(a.pending_purchase_requests || []).map((r) => ({
            key: r.id,
            main: (r.items || []).map((i) => i.name).join(", "),
            sub: `${r.number} • ${r.priority}`,
            tag: r.source === "ai" ? "AI" : "Manual",
          }))}
        />
      </section>
    </div>
  );
}

function AlertCard({ icon: Icon, title, rows, empty, link, testId }) {
  return (
    <div className="surface flex flex-col" data-testid={testId}>
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-semibold">{title}</span>
        <Badge variant="secondary" className="ml-auto font-mono-num text-[10px]">
          {rows.length}
        </Badge>
      </div>
      <div className="flex-1 divide-y divide-border">
        {rows.length === 0 && <div className="px-4 py-6 text-sm text-muted-foreground">{empty}</div>}
        {rows.slice(0, 5).map((r) => (
          <div key={r.key} className="flex items-start gap-2 px-4 py-2.5">
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{r.main}</div>
              <div className="truncate text-xs text-muted-foreground">{r.sub}</div>
            </div>
            <span className="shrink-0 font-mono-num text-xs text-accent">{r.tag}</span>
          </div>
        ))}
      </div>
      <Link
        to={link}
        className="flex items-center gap-1 border-t border-border px-4 py-2 text-xs text-primary hover:underline"
      >
        Open <ArrowUpRight className="h-3 w-3" />
      </Link>
    </div>
  );
}
